import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Heart, Clock, RefreshCw, Wifi, WifiOff, Activity, ChevronRight, ClipboardList, Plus, Share2, Settings, AlertCircle, Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../store/authStore';
import { useDeviceWebSocket } from '../hooks/useDeviceWebSocket';
import { useHeartbeatSound } from '../hooks/useHeartbeatSound';
import { getSessions } from '../api/sessions';
import { getMyDevices } from '../api/devices';
import { SkeletonBlock } from '../components/SkeletonBlock';
export function Dashboard() {
  const navigate = useNavigate();
  const {
    user,
    token
  } = useAuth();
  const {
    isConnected,
    bpm,
    piezoValue,
    waveformData
  } = useDeviceWebSocket(token);
  const {
    soundEnabled,
    toggleSound
  } = useHeartbeatSound(bpm, isConnected);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [device, setDevice] = useState<any>(null);
  const [stats, setStats] = useState({
    avgBpm: 0,
    timeMonitored: 0
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  // Fetch Data
  const fetchData = async () => {
    if (!token) return;
    setError(false);
    try {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const [sessionsRes, devicesRes] = await Promise.all([getSessions(token, {
        from: startOfToday.toISOString()
      }), getMyDevices(token)]);
      setSessions(sessionsRes || []);
      if (devicesRes && devicesRes.length > 0) {
        setDevice(devicesRes[0]);
      }
      // Compute stats
      if (sessionsRes && sessionsRes.length > 0) {
        let totalDuration = 0;
        let totalBpm = 0;
        let bpmCount = 0;
        sessionsRes.forEach((s: any) => {
          if (s.duration) totalDuration += s.duration;
          if (s.avgBpm) {
            totalBpm += s.avgBpm;
            bpmCount++;
          }
        });
        setStats({
          avgBpm: bpmCount > 0 ? Math.round(totalBpm / bpmCount) : 0,
          timeMonitored: totalDuration
        });
      }
    } catch (err) {
      // console.error('Failed to load dashboard data', err)
      setError(true);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [token]);
  // Pull to refresh
  const touchStartY = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.scrollY === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (window.scrollY === 0 && touchStartY.current > 0) {
      const touchEndY = e.changedTouches[0].clientY;
      if (touchEndY - touchStartY.current > 60) {
        setIsRefreshing(true);
        fetchData();
      }
    }
    touchStartY.current = 0;
  };
  // Canvas Waveform Rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const draw = () => {
      const width = canvas.width;
      const height = canvas.height;
      ctx.clearRect(0, 0, width, height);
      if (!isConnected) {
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(0, height / 2);
        ctx.lineTo(width, height / 2);
        ctx.strokeStyle = '#8A8A8A';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#8A8A8A';
        ctx.font = '14px "DM Sans"';
        ctx.textAlign = 'center';
        ctx.fillText('Waiting for signal...', width / 2, height / 2 - 15);
        animationRef.current = requestAnimationFrame(draw);
        return;
      }
      const data = waveformData;
      const step = width / (data.length - 1);
      // Draw Glow
      ctx.beginPath();
      for (let i = 0; i < data.length; i++) {
        const x = i * step;
        // Map 0-4095 to canvas height (invert Y so higher values are up)
        const y = height - data[i] / 4095 * height;
        if (i === 0) ctx.moveTo(x, y);else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = 'rgba(107, 33, 232, 0.3)';
      ctx.lineWidth = 6;
      ctx.lineJoin = 'round';
      ctx.stroke();
      // Draw Main Line
      ctx.beginPath();
      for (let i = 0; i < data.length; i++) {
        const x = i * step;
        const y = height - data[i] / 4095 * height;
        if (i === 0) ctx.moveTo(x, y);else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = '#6B21E8';
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.stroke();
      animationRef.current = requestAnimationFrame(draw);
    };
    // Handle resize
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    draw();
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isConnected, waveformData]);
  // Helpers
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };
  const getFirstName = () => {
    if (!user?.fullName) return 'Mama';
    return user.fullName.split(' ')[0];
  };
  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };
  const formatDuration = (minutes: number) => {
    if (!minutes) return '0h 0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };
  const getRelativeTime = (dateString: string) => {
    if (!dateString) return '--';
    const diff = Date.now() - new Date(dateString).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    return 'Yesterday';
  };
  const getBpmStatus = (currentBpm: number | null) => {
    if (!isConnected || !currentBpm) return {
      label: 'No signal',
      classes: 'bg-border text-text-secondary'
    };
    if (currentBpm < 110) return {
      label: 'Below normal',
      classes: 'bg-danger/15 text-danger'
    };
    if (currentBpm > 160) return {
      label: 'Above normal',
      classes: 'bg-danger/15 text-danger'
    };
    return {
      label: 'Normal range',
      classes: 'bg-accent/20 text-accent'
    };
  };
  const bpmStatus = getBpmStatus(bpm);
  return <main className="w-full h-full flex flex-col" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-861">
      {isRefreshing && <div className="flex justify-center py-2" data-id="element-862">
          <RefreshCw size={20} className="text-primary animate-spin" data-id="element-863" />
        </div>}

      {error && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 flex items-center justify-between" data-id="element-864">
          <div className="flex items-center text-danger" data-id="element-865">
            <AlertCircle size={18} className="mr-2" data-id="element-866" />
            <span className="text-[14px] font-medium" data-id="element-867">
              Some data could not be loaded. Pull down to refresh.
            </span>
          </div>
          <button onClick={fetchData} className="text-danger text-[14px] font-semibold hover:underline" data-id="element-868">
            Retry
          </button>
        </div>}

      {/* Desktop Grid Layout */}
      <div className="flex flex-col xl:grid xl:grid-cols-[1fr_380px] xl:gap-8" data-id="element-869">
        {/* LEFT COLUMN */}
        <div className="flex flex-col space-y-6 xl:space-y-8" data-id="element-870">
          {/* Header */}
          <div className="flex justify-between items-start" data-id="element-871">
            <div data-id="element-872">
              <h1 className="text-[22px] font-semibold text-text-primary" data-id="element-873">
                {getGreeting()}, {getFirstName()}
              </h1>
              <p className="text-[14px] text-text-secondary mt-1" data-id="element-874">
                {formatDate()}
              </p>
            </div>
            <div className="flex items-center space-x-4 xl:hidden" data-id="element-875">
              <button onClick={() => navigate('/app/alerts')} className="relative p-2" aria-label="View alerts" data-id="element-876">
                <Bell size={22} className="text-text-primary" data-id="element-877" />
                {/* Alert badge would go here if we had global alert state */}
              </button>
              <button onClick={() => navigate('/app/profile')} className="w-[36px] h-[36px] bg-primary rounded-full flex items-center justify-center text-white text-[14px] font-semibold" aria-label="View profile" data-id="element-878">
                {getFirstName()[0]}
              </button>
            </div>
          </div>

          {/* Live Monitor Hero Card */}
          <div className="bg-white rounded-[24px] shadow-elevated p-5 w-full" data-id="element-879">
            <div className="flex justify-between items-center mb-4" data-id="element-880">
              <div className="flex items-center" data-id="element-881">
                <div className={`w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-accent animate-pulse' : 'bg-border'}`} data-id="element-882" />
                <h2 className="text-[16px] font-semibold text-text-primary" data-id="element-883">
                  Live Monitor
                </h2>
              </div>
              <div className="flex items-center space-x-2" data-id="element-884">
                <button onClick={toggleSound} className="w-[36px] h-[36px] rounded-full border border-primary text-primary flex items-center justify-center hover:bg-primary/5 transition-colors" aria-label={soundEnabled ? 'Mute heartbeat' : 'Play heartbeat'} data-id="element-885">
                  {soundEnabled ? <Volume2 size={18} data-id="element-886" /> : <VolumeX size={18} data-id="element-887" />}
                </button>
                <button onClick={() => navigate('/app/monitor')} className="border border-primary text-primary text-[13px] font-medium px-[14px] py-[6px] rounded-pill hover:bg-primary/5 transition-colors h-[36px]" data-id="element-888">
                  Full Screen
                </button>
              </div>
            </div>

            <div className="w-full h-[140px] xl:h-[180px] bg-background rounded-[16px] relative overflow-hidden mb-4" data-id="element-889">
              <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" role="img" aria-label="Live fetal heart rate waveform" data-id="element-890" />
            </div>

            <div className="flex items-end justify-between" data-id="element-891">
              <div className="flex items-center space-x-6" data-id="element-892">
                <div className="flex flex-col" data-id="element-893">
                  <motion.span key={bpm} initial={{
                  scale: 1.1
                }} animate={{
                  scale: 1
                }} className="text-[48px] font-bold text-primary leading-none" aria-live="polite" aria-label={`Current BPM: ${isConnected && bpm ? bpm : 'Unknown'}`} data-id="element-894">
                    {isConnected && bpm ? bpm : '--'}
                  </motion.span>
                  <span className="text-[12px] text-text-secondary mt-1" data-id="element-895">
                    BPM
                  </span>
                </div>

                <div className="w-[1px] h-[40px] bg-border" data-id="element-896" />

                <div className="flex flex-col" data-id="element-897">
                  <span className="text-[12px] text-text-secondary mb-1" data-id="element-898">
                    Min
                  </span>
                  <span className="text-[20px] font-semibold text-text-primary leading-none" data-id="element-899">
                    --
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-900">
                  <span className="text-[12px] text-text-secondary mb-1" data-id="element-901">
                    Max
                  </span>
                  <span className="text-[20px] font-semibold text-text-primary leading-none" data-id="element-902">
                    --
                  </span>
                </div>
              </div>

              <div className={`px-3 py-1 rounded-pill text-[13px] font-medium ${bpmStatus.classes}`} data-id="element-903">
                {bpmStatus.label}
              </div>
            </div>
          </div>

          {/* Recent Sessions (Desktop Left Col, Mobile Stacked) */}
          <div className="w-full" data-id="element-904">
            <div className="flex justify-between items-center mb-4" data-id="element-905">
              <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-906">
                Recent Sessions
              </h2>
              <button onClick={() => navigate('/app/history')} className="text-[14px] text-primary hover:underline" data-id="element-907">
                See all
              </button>
            </div>

            {isLoading ? <div className="space-y-3" data-id="element-908">
                {[1, 2, 3].map(i => <SkeletonBlock key={i} height="72px" data-id="element-909" />)}
              </div> : sessions.length > 0 ? <div className="space-y-3" data-id="element-910">
                {sessions.slice(0, 3).map((session, i) => <div key={session.id || i} onClick={() => navigate(`/app/history/${session.id}`)} className="bg-white rounded-[16px] shadow-sm p-4 flex items-center cursor-pointer hover:-translate-y-[2px] hover:shadow-card transition-all" data-id="element-911">
                    <div className="w-[40px] h-[40px] bg-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0" data-id="element-912">
                      <Activity size={18} className="text-white" data-id="element-913" />
                    </div>
                    <div className="flex-1" data-id="element-914">
                      <h3 className="text-[14px] font-semibold text-text-primary" data-id="element-915">
                        {new Date(session.createdAt || Date.now()).toLocaleDateString('en-US', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short'
                  })}
                      </h3>
                      <p className="text-[13px] text-text-secondary mt-0.5" data-id="element-916">
                        {formatDuration(session.duration)} · Avg{' '}
                        {session.avgBpm || '--'} BPM
                      </p>
                    </div>
                    <ChevronRight size={20} className="text-text-secondary" data-id="element-917" />
                  </div>)}
              </div> : <div className="bg-secondary rounded-[16px] p-6 flex flex-col items-center justify-center text-center" data-id="element-918">
                <ClipboardList size={40} className="text-primary mb-3" data-id="element-919" />
                <h3 className="text-[16px] font-semibold text-text-primary mb-1" data-id="element-920">
                  No sessions yet
                </h3>
                <p className="text-[14px] text-text-secondary max-w-[200px]" data-id="element-921">
                  Start monitoring and your sessions will appear here.
                </p>
              </div>}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col space-y-6 xl:space-y-8 mt-6 xl:mt-0" data-id="element-922">
          {/* Today's Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-2 gap-4" data-id="element-923">
            {/* Stat 1 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-924">
              <div className="w-[32px] h-[32px] bg-primary/10 rounded-full flex items-center justify-center mb-3" data-id="element-925">
                <Heart size={16} className="text-primary" data-id="element-926" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-927" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-928">
                  {stats.avgBpm || '--'}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-929">
                Avg BPM today
              </span>
            </div>

            {/* Stat 2 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-930">
              <div className="w-[32px] h-[32px] bg-accent/20 rounded-full flex items-center justify-center mb-3" data-id="element-931">
                <Clock size={16} className="text-accent" data-id="element-932" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-933" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-934">
                  {formatDuration(stats.timeMonitored)}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-935">
                Monitored today
              </span>
            </div>

            {/* Stat 3 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-936">
              <div className="w-[32px] h-[32px] bg-amber-500/15 rounded-full flex items-center justify-center mb-3" data-id="element-937">
                <RefreshCw size={16} className="text-amber-500" data-id="element-938" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="80%" className="mb-1" data-id="element-939" /> : <span className="text-[20px] font-bold text-text-primary leading-tight mb-1 truncate" data-id="element-940">
                  {getRelativeTime(device?.lastSeenAt)}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-941">Last sync</span>
            </div>

            {/* Stat 4 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-942">
              <div className={`w-[32px] h-[32px] rounded-full flex items-center justify-center mb-3 ${device ? 'bg-primary/10' : 'bg-border'}`} data-id="element-943">
                {device ? <Wifi size={16} className="text-primary" data-id="element-944" /> : <WifiOff size={16} className="text-text-secondary" data-id="element-945" />}
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="70%" className="mb-1" data-id="element-946" /> : <span className={`text-[18px] font-bold leading-tight mb-1 ${device ? 'text-accent' : 'text-danger'}`} data-id="element-947">
                  {device ? 'Connected' : 'Offline'}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-948">
                Belt status
              </span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="w-full" data-id="element-949">
            <h2 className="text-[16px] font-semibold text-text-primary mb-3 xl:hidden" data-id="element-950">
              Quick Actions
            </h2>
            <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap md:gap-3 hide-scrollbar space-x-3 md:space-x-0" data-id="element-951">
              <button onClick={() => navigate('/app/setup')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-952">
                <Plus size={16} className="text-primary mr-2" data-id="element-953" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-954">
                  Add Device
                </span>
              </button>
              <button onClick={() => navigate('/app/history')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-955">
                <Share2 size={16} className="text-primary mr-2" data-id="element-956" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-957">
                  Share Report
                </span>
              </button>
              <button onClick={() => navigate('/app/alerts')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-958">
                <Bell size={16} className="text-primary mr-2" data-id="element-959" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-960">
                  View Alerts
                </span>
              </button>
              <button onClick={() => navigate('/app/profile')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-961">
                <Settings size={16} className="text-primary mr-2" data-id="element-962" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-963">
                  Settings
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-964">{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </main>;
}